import { Controller } from "@hotwired/stimulus"
import dagre from "@dagrejs/dagre"

// MC-992 / MC-1087: lays out and draws a task's dependency chain at 1:1.
//
// dagre computes layout only and renders nothing (ADR: the node must be
// the real board card, so React Flow / Cytoscape are out — see the task).
// This controller measures each server-rendered card, hands the sizes +
// edges to dagre (laid out left-to-right), absolutely positions the cards
// at the coordinates it returns, strokes the edges as SVG behind the cards
// following dagre's bend points (arrowhead at the blocked end), and sizes
// the world to dagre's computed bounds.
//
// The graph is never scaled — cards read at exactly the size the board
// renders them — and the operator moves around with the browser's own
// scrollbars/trackpad. There is no pan/zoom state and no window listeners,
// so a graph opened, closed and re-opened (Turbo reuses the page) leaves
// nothing behind.
const SVG_NS = "http://www.w3.org/2000/svg"

export default class extends Controller {
  static targets = ["world", "edges", "node"]
  static values = { edges: Array }

  connect() {
    this.markerId = `dep-graph-arrow-${Math.random().toString(36).slice(2)}`
    this.layout()
  }

  // Measure each rendered card, run dagre left-to-right, position the cards,
  // draw the edges, then size the world to the computed bounds so the
  // viewport scrolls to reach the whole graph.
  layout() {
    if (this.nodeTargets.length === 0) return

    const graph = new dagre.graphlib.Graph()
    // rankdir "LR": the 300px card width is the rank axis (ranks separated
    // horizontally, so a blocked task sits to the right of its blocker) and
    // the variable card heights the cross axis (siblings stacked vertically).
    // ranksep 120 is the horizontal gap between a blocker and what it blocks
    // — wide enough for a clearly horizontal edge between 300px cards;
    // nodesep 48 the vertical gap that keeps stacked sibling branches apart.
    graph.setGraph({ rankdir: "LR", nodesep: 48, ranksep: 120, marginx: 20, marginy: 20 })
    graph.setDefaultEdgeLabel(() => ({}))

    this.nodeTargets.forEach((el) => {
      graph.setNode(el.dataset.nodeId, {
        width: el.offsetWidth,
        height: el.offsetHeight,
        el
      })
    })

    this.edgesValue.forEach((edge) => {
      const from = String(edge.from)
      const to = String(edge.to)
      if (graph.hasNode(from) && graph.hasNode(to)) graph.setEdge(from, to)
    })

    dagre.layout(graph)

    // Cards: dagre gives node centres; the wrapper is absolute at 0,0, so
    // translate it to (centre − half-size).
    graph.nodes().forEach((id) => {
      const node = graph.node(id)
      const x = node.x - node.width / 2
      const y = node.y - node.height / 2
      node.el.style.transform = `translate(${x}px, ${y}px)`
    })

    const { width, height } = graph.graph()
    this.graphWidth = width || 0
    this.graphHeight = height || 0
    // Size the world to dagre's bounds so the viewport has something to
    // scroll — at 1:1 the scrollable area is exactly the graph.
    this.worldTarget.style.width = `${this.graphWidth}px`
    this.worldTarget.style.height = `${this.graphHeight}px`

    this.drawEdges(graph)
  }

  drawEdges(graph) {
    const svg = this.edgesTarget
    svg.setAttribute("width", this.graphWidth)
    svg.setAttribute("height", this.graphHeight)
    svg.replaceChildren()

    // A single arrowhead marker, reused by every edge; sized in user-space
    // units so it renders at a fixed size against the graph's 1:1 world.
    const defs = document.createElementNS(SVG_NS, "defs")
    const marker = document.createElementNS(SVG_NS, "marker")
    marker.setAttribute("id", this.markerId)
    marker.setAttribute("markerWidth", "9")
    marker.setAttribute("markerHeight", "9")
    marker.setAttribute("refX", "8")
    marker.setAttribute("refY", "3")
    marker.setAttribute("orient", "auto")
    marker.setAttribute("markerUnits", "userSpaceOnUse")
    const head = document.createElementNS(SVG_NS, "path")
    head.setAttribute("d", "M0,0 L8,3 L0,6 Z")
    head.setAttribute("class", "tasks-dependency-graph__arrow")
    marker.appendChild(head)
    defs.appendChild(marker)
    svg.appendChild(defs)

    graph.edges().forEach((edgeObj) => {
      const points = graph.edge(edgeObj).points
      if (!points || points.length === 0) return
      const d = points
        .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
        .join(" ")
      const path = document.createElementNS(SVG_NS, "path")
      path.setAttribute("d", d)
      // Arrowhead at the path end — the blocked end, since edges run
      // blocking → blocked — so a blocker visibly points at what it blocks.
      path.setAttribute("marker-end", `url(#${this.markerId})`)
      svg.appendChild(path)
    })
  }
}
